<template>
  <v-card outlined class="rounded-lg">
    <v-card-text>
      <!-- Header -->
      <div class="d-flex align-center mb-4">
        <v-avatar size="40" color="primary" class="mr-3" rounded>
          <v-icon color="white">mdi-medal-outline</v-icon>
        </v-avatar>
        <div>
          <div class="subtitle-2 font-weight-bold">Recognition</div>
          <div class="caption grey--text">Celebrate great work</div>
        </div>
      </div>

      <!-- Tab buttons -->
      <div class="d-flex align-center mb-4" style="gap:8px;">
        <v-btn
          depressed small
          :color="tab === 0 ? 'primary' : 'grey lighten-3'"
          :class="tab === 0 ? 'white--text' : 'grey--text text--darken-1'"
          class="text-none rounded-lg"
          @click="tab = 0"
        >
          <v-icon small left>mdi-inbox</v-icon>
          Received
        </v-btn>
        <v-btn
          depressed small
          :color="tab === 1 ? 'primary' : 'grey lighten-3'"
          :class="tab === 1 ? 'white--text' : 'grey--text text--darken-1'"
          class="text-none rounded-lg"
          @click="tab = 1"
        >
          <v-icon small left>mdi-send</v-icon>
          Give Praise
        </v-btn>
        <v-btn
          depressed small
          :color="tab === 2 ? 'primary' : 'grey lighten-3'"
          :class="tab === 2 ? 'white--text' : 'grey--text text--darken-1'"
          class="text-none rounded-lg"
          @click="tab = 2"
        >
          <v-icon small left>mdi-gift-outline</v-icon>
          Given
        </v-btn>
      </div>

      <!-- Received -->
      <div v-if="tab === 0">
        <div v-if="received.length === 0" class="caption grey--text text-center py-6">No praise received yet.</div>
        <div v-for="p in received" :key="p.id" class="pa-3 rounded-lg mb-3" style="background:#f5f5f5;border:1px solid #e0e0e0;">
          <div class="d-flex align-center justify-space-between mb-2">
            <v-chip x-small color="orange lighten-4" class="orange--text text--darken-3 font-weight-medium">
              {{ p.category }}
            </v-chip>
            <span class="caption grey--text">From {{ p.from }} • {{ formatDate(p.date) }}</span>
          </div>
          <div class="body-2" style="line-height:1.6;">{{ p.message }}</div>
        </div>
      </div>

      <!-- Give Praise -->
      <div v-if="tab === 1">
        <div class="caption grey--text text-uppercase font-weight-medium mb-1">Recipient</div>
        <v-autocomplete v-model="draft.recipient_id" :items="userOptions" item-text="text" item-value="value" dense outlined hide-details class="mb-3 rounded-lg" placeholder="Search teammate..." clearable no-data-text="No users found" />
        <div class="caption grey--text text-uppercase font-weight-medium mb-1">Category</div>
        <v-select v-model="draft.category" :items="categories" dense outlined hide-details class="mb-3 rounded-lg" />
        <div class="caption grey--text text-uppercase font-weight-medium mb-1">Message</div>
        <v-textarea v-model="draft.message" dense outlined hide-details rows="3" class="mb-4 rounded-lg" />
        <v-btn color="primary" depressed block large class="text-none rounded-lg" :loading="sending" @click="sendPraise">
          <v-icon left>mdi-send</v-icon>
          Send Praise
        </v-btn>
      </div>

      <!-- Given -->
      <div v-if="tab === 2">
        <div v-if="given.length === 0" class="caption grey--text text-center py-6">You haven't given any praise yet.</div>
        <div v-for="p in given" :key="p.id" class="pa-3 rounded-lg mb-3" style="background:#f5f5f5;border:1px solid #e0e0e0;">
          <div class="d-flex align-center justify-space-between mb-2">
            <v-chip x-small color="orange lighten-4" class="orange--text text--darken-3 font-weight-medium">
              {{ p.category }}
            </v-chip>
            <span class="caption grey--text">To {{ p.to }} • {{ formatDate(p.date) }}</span>
          </div>
          <div class="body-2" style="line-height:1.6;">{{ p.message }}</div>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: 'RecognitionCard',
  inject: ['users'],
  data() {
    return {
      loading: false,
      sending: false,
      tab: 0,
      draft: { recipient_id: null, category: '', message: '' },
      categories: ['Leadership', 'Teamwork', 'Innovation', 'Mentoring', 'Delivery', 'Communication'],
      received: [],
      given: [],
    };
  },
  computed: {
    userOptions() {
      return (this.users() || []).map(u => ({ text: u.name, value: u.id }));
    },
  },
  mounted() {
    this.load();
    // Sync with global growth bus so recognition list refreshes whenever
    // praise is sent/received elsewhere.
    this.$root.$on('growth:refresh', this.load);
  },
  beforeDestroy() {
    this.$root.$off('growth:refresh', this.load);
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const [recRes, givRes] = await Promise.all([
          this.$axios.get('api/employee-growth/recognition/received'),
          this.$axios.get('api/employee-growth/recognition/given'),
        ]);
        if (recRes.data.response) {
          this.received = recRes.data.data.map(r => ({ id: r.id, from: r.sender?.name || '?', category: r.category, message: r.message, date: r.created_at }));
        }
        if (givRes.data.response) {
          this.given = givRes.data.data.map(r => ({ id: r.id, to: r.recipient?.name || '?', category: r.category, message: r.message, date: r.created_at }));
        }
      } finally {
        this.loading = false;
      }
    },
    async sendPraise() {
      if (!this.draft.recipient_id) { this.$toast && this.$toast.error('Pick a teammate'); return; }
      if (!this.draft.message.trim()) { this.$toast && this.$toast.error('Add a message'); return; }
      this.sending = true;
      try {
        const res = await this.$axios.post('api/employee-growth/recognition', {
          recipient_id: this.draft.recipient_id,
          category: this.draft.category || 'General',
          message: this.draft.message,
        });
        if (res.data.response) {
          const r = res.data.data;
          this.given.unshift({ id: r.id, to: r.recipient?.name || '?', category: r.category, message: r.message, date: r.created_at });
          this.draft = { recipient_id: null, category: '', message: '' };
          this.tab = 2;
          this.$toast && this.$toast.success('Praise sent!');
        }
      } catch (e) {
        this.$toast && this.$toast.error('Failed to send praise');
      } finally {
        this.sending = false;
      }
    },
    formatDate(iso) {
      // $date/$tz so this follows the display timezone and clock settings.
      const d = this.$date(iso);
      if (!d.isValid()) return iso;
      return d.format(`MMM D, YYYY ${this.$tz.time}`);
    },
  },
};
</script>
